import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FileImage, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

/** Small numeric tile used in the migration totals row. */
export function Stat({ label, value, color }) {
  return (
    <div className="p-2 rounded bg-[#000B18] text-center border border-white/5">
      <p className="font-outfit text-xl font-bold" style={{ color }}>{value}</p>
      <p className="text-[#5C7599] text-[10px] uppercase tracking-wide mt-0.5">{label}</p>
    </div>
  );
}

/** MissingFilesAudit — scan all DB file references and list ones whose
 *  binary is missing from storage (common after an ephemeral disk wipe).
 *  Exports the full result to CSV so admins can request re-uploads.
 */
export function MissingFilesAudit({ api }) {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const runAudit = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/api/admin/storage/audit-missing-files');
      setReport(data);
    } catch (e) {
      toast.error('Audit failed: ' + (e.response?.data?.detail || e.message));
    }
    setRunning(false);
  };

  const downloadCsv = () => {
    if (!report?.items?.length) return;
    const rows = [
      ['collection', 'doc_id', 'doc_email', 'doc_name', 'doc_role', 'field', 'reason', 'url'],
      ...report.items.map(r => [r.collection, r.doc_id, r.doc_email, r.doc_name, r.doc_role, r.field, r.reason, r.url]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'missing-files-report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="bg-[#041A3B] border-white/10 mt-4" data-testid="missing-files-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <FileImage className="w-5 h-5 text-[#FFEA00]" />
          Missing Files Audit
        </CardTitle>
        <CardDescription className="text-[#A0B4CC]">
          Scan every file reference in the database (IDs, selfies, profile photos, merchant logos, product images, etc.)
          and list the ones whose binary is missing from storage — typically wiped by a past redeploy of ephemeral disk.
          Export to CSV so you know exactly which users/merchants to ask for a re-upload.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          onClick={runAudit} disabled={running}
          className="bg-[#FFEA00] text-[#000B18] hover:bg-[#FFEA00]/90"
          data-testid="run-missing-files-audit-btn"
        >
          {running ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning…</>) : (<><Search className="w-4 h-4 mr-2" /> Run Audit</>)}
        </Button>

        {report && (
          <div className="p-3 rounded-lg bg-[#000B18] border border-white/10 space-y-2 text-sm">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-[#A0B4CC]">Scanned: <b className="text-white">{report.total_scanned}</b></span>
              <span className="text-[#A0B4CC]">Missing: <b className={report.missing_count > 0 ? 'text-[#FF1744]' : 'text-[#00E676]'}>{report.missing_count}</b></span>
              {report.missing_count > 0 && (
                <Button size="sm" variant="outline" onClick={downloadCsv}
                  className="ml-auto border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:text-[#00E5FF]"
                  data-testid="download-missing-csv">
                  Download CSV
                </Button>
              )}
            </div>
            {Object.keys(report.by_collection || {}).length > 0 && (
              <div className="text-xs text-[#A0B4CC]">
                By collection: {Object.entries(report.by_collection).map(([k, v]) => `${k}: ${v}`).join(' · ')}
              </div>
            )}
            {report.missing_count === 0 ? (
              <p className="text-[#00E676] text-xs">All files accounted for. Nothing to recover.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto mobile-scroll-wrap border border-white/5 rounded text-xs" data-testid="missing-files-list">
                <table className="w-full text-left">
                  <thead className="bg-[#041A3B] text-[#5C7599] sticky top-0">
                    <tr>
                      <th className="p-2">Collection</th>
                      <th className="p-2">Who</th>
                      <th className="p-2">Field</th>
                      <th className="p-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.slice(0, 200).map((r, i) => (
                      <tr key={`${r.collection}-${r.doc_id}-${r.field}-${i}`} className="border-t border-white/5 text-[#A0B4CC]">
                        <td className="p-2 font-mono">{r.collection}</td>
                        <td className="p-2 break-all">{r.doc_email || r.doc_name || r.doc_id}</td>
                        <td className="p-2 font-mono">{r.field}</td>
                        <td className="p-2 text-[#FF1744]">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.items.length > 200 && (
                  <p className="p-2 text-[#5C7599] text-center">Showing first 200 of {report.items.length} — download CSV for full list.</p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MissingFilesAudit;
